import { setupI18n, type Messages } from '@lingui/core';
import { createBrowserLanguageEnvironment, type LanguageEnvironment } from './environment';
import { isLanguagePreference, resolveLocale, type LanguagePreference, type SupportedLocale } from './locale';
import { catalogs as defaultCatalogs } from './mainCatalogs';

export interface LanguageState {
  preference: LanguagePreference;
  effectiveLocale: SupportedLocale;
  persistenceError: Error | null;
}

export const createLanguageRuntime = ({
  environment = createBrowserLanguageEnvironment(),
  catalogs = defaultCatalogs,
}: {
  environment?: LanguageEnvironment;
  catalogs?: Record<SupportedLocale, Messages>;
} = {}) => {
  const readPreference = (): Pick<LanguageState, 'preference' | 'persistenceError'> => {
    try {
      const value = environment.readPreference();
      if (value === null) return { preference: 'system', persistenceError: null };
      if (!isLanguagePreference(value)) throw new Error('Unsupported saved language preference.');
      return { preference: value, persistenceError: null };
    } catch (cause) {
      const error = new Error('Unable to read the saved language preference.', { cause });
      environment.reportError(error);
      return { preference: 'system', persistenceError: error };
    }
  };

  const saved = readPreference();
  let state: LanguageState = {
    ...saved,
    effectiveLocale: resolveLocale(saved.preference, environment.getLanguages()),
  };
  const i18n = setupI18n({ locale: state.effectiveLocale, messages: catalogs });
  environment.setDocumentLanguage(state.effectiveLocale);
  const listeners = new Set<() => void>();
  const update = (preference: LanguagePreference, persistenceError: Error | null) => {
    const effectiveLocale = resolveLocale(preference, environment.getLanguages());
    if (state.preference === preference && state.effectiveLocale === effectiveLocale
      && state.persistenceError === persistenceError) return;
    state = { preference, effectiveLocale, persistenceError };
    environment.setDocumentLanguage(effectiveLocale);
    if (i18n.locale !== effectiveLocale) i18n.activate(effectiveLocale);
    listeners.forEach(listener => listener());
  };

  let connections = 0;
  let disconnect: (() => void) | undefined;
  return {
    i18n,
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    setPreference: (preference: LanguagePreference) => {
      if (!isLanguagePreference(preference)) throw new Error('Unsupported language preference.');
      let persistenceError: Error | null = null;
      try {
        environment.writePreference(preference);
      } catch (cause) {
        persistenceError = new Error('Unable to save the language preference on this device.', { cause });
        environment.reportError(persistenceError);
      }
      update(preference, persistenceError);
    },
    connect: () => {
      if (connections++ === 0) {
        disconnect = environment.subscribe(() => {
          const saved = readPreference();
          update(saved.preference, saved.persistenceError);
        }, () => {
          if (state.preference === 'system') update('system', state.persistenceError);
        });
        // React may mount after a browser event has already happened. Catch up
        // after subscribing, while keeping an unsaved current-tab choice intact.
        if (state.persistenceError === null) {
          const saved = readPreference();
          update(saved.preference, saved.persistenceError);
        } else if (state.preference === 'system') {
          update('system', state.persistenceError);
        }
      }
      let connected = true;
      return () => {
        if (!connected) return;
        connected = false;
        if (--connections === 0) {
          disconnect?.();
          disconnect = undefined;
        }
      };
    },
  };
};

export type LanguageRuntime = ReturnType<typeof createLanguageRuntime>;
